/**
 * SSC Request Builder
 *
 * Pure function: checkout state → GetInsurancesRequest.
 * Encapsulates all SSC-specific data mapping:
 * - ISO dates → /Date(ms)/ .NET format
 * - rooms → Services[] (one per room)
 * - passengers → Participants[] (global, with age)
 * - ServiceTypeCode = roomCode + boardCode concat
 * - BookingCode = productCode (GIATA code)
 */
import { SSC_CONFIG } from '@/services/api/ssc/config';
import { toUtcMidnight } from '@/utils/formatting/dateFormatter';
import type { GetInsurancesRequest, SscService, SscParticipant } from '@/types/schemas/ssc';

export interface SelectedExtra {
  requirement: string;
  bookingCode: string;
  serviceTypeCode: string;
  from?: string; // /Date(ms)/ format, falls back to accommodation dates
  to?: string;
}

export interface SscRequestParams {
  roomCodes: string[];
  boardCode: string;
  productCode: string;
  catalogCode: string;
  checkIn: Date;
  checkOut: Date;
  passengers: Array<{ age: number; roomNumber: number; isAdult: boolean }>;
  selectedExtras?: SelectedExtra[];
}

function toNetDate(date: Date): string {
  return `/Date(${date.getTime()})/`;
}

/**
 * Build participant assignment string for a room.
 * SSC expects "1-2" (range) format for consecutive participants.
 */
function buildParticipantAssignment(startId: number, count: number): string {
  if (count === 1) return String(startId);
  return `${startId}-${startId + count - 1}`;
}

/**
 * Build the complete GetInsurances request body from checkout state.
 * Each room becomes one Service entry in the Basket.
 */
export function buildSscInsurancesRequest(params: SscRequestParams): GetInsurancesRequest {
  const {
    roomCodes,
    boardCode,
    productCode,
    catalogCode,
    checkIn,
    checkOut,
    passengers,
    selectedExtras,
  } = params;

  // Sort passengers by room number to ensure ParticipantAssignment indices match Participants[]
  const sortedPassengers = [...passengers].sort((a, b) => a.roomNumber - b.roomNumber);

  // Calendar dates anchored at UTC midnight: checkIn/checkOut are local-midnight Dates
  // (from the checkout URL parse); raw getTime() would land on the previous day for
  // CET/CEST (VAM-258 bug class). BookingDateTime below stays a true timestamp.
  const fromNet = toNetDate(toUtcMidnight(checkIn));
  const toNet = toNetDate(toUtcMidnight(checkOut));
  const allParticipantsAssignment = buildParticipantAssignment(1, sortedPassengers.length);

  // Build Services[] - one entry per room
  const services: SscService[] = roomCodes.map((roomCode, roomIndex) => {
    const roomNumber = roomIndex + 1;
    const roomPassengerCount = sortedPassengers.filter((p) => p.roomNumber === roomNumber).length;

    // Calculate global start ID for this room's participants (1-based)
    const globalStartId = sortedPassengers.filter((p) => p.roomNumber < roomNumber).length + 1;

    return {
      Requirement: 'H',
      BookingCode: productCode,
      ServiceTypeCode: `${roomCode}${boardCode}`,
      From: fromNet,
      To: toNet,
      Amount: 1, // always 1 room per service entry
      ParticipantAssignment: buildParticipantAssignment(globalStartId, roomPassengerCount),
      CatalogCode: catalogCode,
      UserDefinedService: false,
    };
  });

  // Append selected extras as additional services
  if (selectedExtras?.length) {
    for (const extra of selectedExtras) {
      services.push({
        Requirement: extra.requirement,
        BookingCode: extra.bookingCode,
        ServiceTypeCode: extra.serviceTypeCode,
        From: extra.from || fromNet,
        To: extra.to || toNet,
        Amount: 1,
        ParticipantAssignment: allParticipantsAssignment,
        CatalogCode: catalogCode,
        UserDefinedService: false,
      });
    }
  }

  // Build Participants[] - all passengers in room order (matching Service assignments)
  const sscParticipants: SscParticipant[] = sortedPassengers.map((p) => ({
    Salutation: 'H',
    Age: p.age ?? (p.isAdult ? 35 : 10),
    NoInsuranceCalcForThisParticipant: false,
  }));

  return {
    Login: {},
    Basket: {
      Services: services,
      Participants: sscParticipants,
      CatalogCode: catalogCode,
      Agency: SSC_CONFIG.AGENCY_NUMBER,
      Language: SSC_CONFIG.LANGUAGE,
    },
    BookingDateTime: toNetDate(new Date()),
    BookingSource: SSC_CONFIG.BOOKING_SOURCE,
    SearchText: '',
    IsInteractiveRequest: false,
  };
}
